// Calls to the server. Every request carries the device id; errors carry the Dutch message of the server.

import { getMeta, setMeta } from "./db";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** True when the server could not answer: offline, timeout, or the server is down or restarting (5xx from Caddy). */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof DOMException && (error.name === "AbortError" || error.name === "TimeoutError")) return true;
  return error instanceof ApiError && (error.status === 0 || error.status >= 500);
}

let deviceIdPromise: Promise<string> | null = null;

export function deviceId(): Promise<string> {
  deviceIdPromise ??= (async () => {
    const existing = await getMeta<string>("deviceId");
    if (existing) return existing;
    const id = crypto.randomUUID();
    await setMeta("deviceId", id);
    return id;
  })();
  return deviceIdPromise;
}

export async function api<T>(path: string, options: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const headers: Record<string, string> = { "X-Device-Id": await deviceId() };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  const response = await fetch(`/api${path}`, {
    method: options.method ?? (options.body === undefined ? "GET" : "POST"),
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    credentials: "same-origin",
    signal: options.signal,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data && typeof data.error === "string" ? data.error : "Er ging iets mis. Probeer het opnieuw.";
    throw new ApiError(message, response.status);
  }
  return data as T;
}
